import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";
import { Check, Sparkles, Share2, Newspaper, BarChart3, X } from "lucide-react";
import { useMemo, useState } from "react";
import { MarketingNav } from "@/components/Marketing/MarketingNav";
import { MarketingFooter } from "@/components/Marketing/MarketingFooter";

type Currency = "USD" | "KES" | "NGN" | "ZAR";
type OrgType = "standard" | "association" | "journalist" | "individual";
type Cycle = "monthly" | "annual";

const FX: Record<Currency, number> = { USD: 1, KES: 130, NGN: 1600, ZAR: 18 };
const SYMBOLS: Record<Currency, string> = { USD: "$", KES: "KES ", NGN: "₦", ZAR: "R" };
const ANNUAL_DISCOUNT = 0.83; // 17% off

function formatPrice(usd: number, currency: Currency, cycle: Cycle) {
  if (usd === 0) return null;
  const monthly = cycle === "annual" ? usd * ANNUAL_DISCOUNT : usd;
  const val = Math.round(monthly * FX[currency]);
  return `${SYMBOLS[currency]}${val.toLocaleString()}`;
}

const ORG_TYPE_DISPLAY: Record<OrgType, { label: string; description: string }> = {
  standard:    { label: "Organisation",    description: "Government bodies, ministries, corporate comms teams and agencies" },
  association: { label: "Association",     description: "Membership bodies, journalist associations, trade unions and industry groups" },
  journalist:  { label: "Press / Newsroom", description: "Press agencies, media houses, newsrooms and broadcasters" },
  individual:  { label: "Individual",      description: "Freelance journalists and independent media professionals" },
};

const PLAN_DISPLAY: Record<string, {
  label: string;
  tagline: string;
  highlight: boolean;
  cta: string;
  ctaHref: string;
  highlightColor?: string;
}> = {
  trial:        { label: "Trial",        tagline: "Try everything free for 14 days",              highlight: false, cta: "Start Free Trial", ctaHref: "/auth?mode=signup" },
  individual:   { label: "Individual",   tagline: "Built for solo journalists and freelancers",   highlight: false, cta: "Get Started",       ctaHref: "/auth?mode=signup" },
  starter:      { label: "Starter",      tagline: "For growing newsrooms and comms teams",        highlight: true,  cta: "Get Started",       ctaHref: "/auth?mode=signup" },
  professional: { label: "Professional", tagline: "Full analytics, awards, and AI at scale",      highlight: false, cta: "Get Started",       ctaHref: "/auth?mode=signup" },
  enterprise:   { label: "Enterprise",   tagline: "Custom infrastructure for large organisations",highlight: false, cta: "Contact Sales",     ctaHref: "/enterprise" },
};

const PRESS_MODULE_KEYS = new Set(["media_registry","accreditation","events","awards","organogram","media_kits","journalist_portal"]);

function planServices(modules: string[] | null | undefined) {
  const mods = modules ?? [];
  return {
    social:    mods.includes("social_media"),
    press:     mods.some(m => PRESS_MODULE_KEYS.has(m)),
    analytics: mods.includes("coverage_analytics"),
  };
}

const SERVICE_BADGES = [
  { key: "social"    as const, label: "Social Hub",         icon: Share2 },
  { key: "press"     as const, label: "Press & Journalist", icon: Newspaper },
  { key: "analytics" as const, label: "Analytics",          icon: BarChart3 },
];

const PAYMENT_METHODS = ["M-Pesa", "Airtel Money", "MTN Mobile Money", "Visa / Mastercard", "PayPal", "Flutterwave"];

const Pricing = () => {
  const [currency, setCurrency]   = useState<Currency>("USD");
  const [orgType, setOrgType]     = useState<OrgType>("standard");
  const [cycle, setCycle]         = useState<Cycle>("monthly");

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ["plan-limits-public"],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("plan_limits")
        .select("*")
        .order("sort_order", { ascending: true });
      return (data ?? []) as any[];
    },
  });

  const visiblePlans = useMemo(
    () => plans.filter((p: any) => (p.org_types ?? []).includes(orgType)),
    [plans, orgType]
  );

  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />

      <section className="container mx-auto px-4 pt-32 pb-16 lg:pt-44">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
            <Sparkles className="w-3 h-3" /> Simple, transparent pricing
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Choose your plan</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Social Hub, Press &amp; Journalist, and Analytics — scaled to who you are
          </p>
        </div>

        {/* Billing cycle toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-1 bg-muted rounded-lg p-1">
            <button
              onClick={() => setCycle("monthly")}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                cycle === "monthly" ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setCycle("annual")}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                cycle === "annual" ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Annual
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
                Save 17%
              </span>
            </button>
          </div>
        </div>

        {/* Client type selector */}
        <div className="flex flex-col items-center gap-2 mb-10">
          <span className="text-sm text-muted-foreground">I am a / an:</span>
          <div className="flex flex-wrap items-center justify-center gap-1.5">
            {(Object.keys(ORG_TYPE_DISPLAY) as OrgType[]).map(t => (
              <Button
                key={t}
                variant={orgType === t ? "default" : "outline"}
                size="sm"
                onClick={() => setOrgType(t)}
              >
                {ORG_TYPE_DISPLAY[t].label}
              </Button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground max-w-md text-center">
            {ORG_TYPE_DISPLAY[orgType].description}
          </p>
        </div>

        {/* Currency selector */}
        <div className="flex items-center justify-center gap-2 mb-10">
          <span className="text-sm text-muted-foreground">Currency:</span>
          <div className="flex gap-1">
            {(["USD", "KES", "NGN", "ZAR"] as Currency[]).map(curr => (
              <Button
                key={curr}
                variant={currency === curr ? "default" : "outline"}
                size="sm"
                onClick={() => setCurrency(curr)}
              >
                {curr}
              </Button>
            ))}
          </div>
        </div>

        {/* Plan cards */}
        {isLoading ? (
          <div className="grid md:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[520px] rounded-2xl" />
            ))}
          </div>
        ) : (
          <div className={`grid gap-6 max-w-6xl mx-auto ${
            visiblePlans.length <= 2 ? "md:grid-cols-2 max-w-2xl"
            : visiblePlans.length === 3 ? "md:grid-cols-3"
            : "md:grid-cols-4"
          }`}>
            {visiblePlans.map((plan: any) => {
              const display  = PLAN_DISPLAY[plan.plan] ?? { label: plan.plan, tagline: "", highlight: false, cta: "Get Started", ctaHref: "/auth?mode=signup" };
              const services = planServices(plan.modules);
              const priceStr = formatPrice(plan.price_usd_monthly, currency, cycle);
              const isFree   = plan.price_usd_monthly === 0;
              const isCustom = isFree && plan.plan !== "trial";

              return (
                <Card
                  key={plan.plan}
                  className={`p-7 flex flex-col relative ${
                    display.highlight ? "border-2 border-primary shadow-xl scale-[1.02]" : ""
                  }`}
                >
                  {display.highlight && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap">
                      Most Popular
                    </Badge>
                  )}

                  {/* Plan name + tagline */}
                  <div className="mb-5">
                    <h3 className="text-xl font-bold mb-1">{display.label}</h3>
                    <p className="text-xs text-muted-foreground mb-4 leading-relaxed">{display.tagline}</p>

                    {/* Price */}
                    <div className="mb-1">
                      {isFree && plan.plan === "trial" ? (
                        <span className="text-3xl font-bold">Free</span>
                      ) : isCustom ? (
                        <span className="text-3xl font-bold">Custom</span>
                      ) : (
                        <div className="flex items-end gap-1">
                          <span className="text-3xl font-bold">{priceStr}</span>
                          <span className="text-sm text-muted-foreground mb-1">/mo</span>
                        </div>
                      )}
                    </div>

                    {cycle === "annual" && !isFree && (
                      <p className="text-[11px] text-green-600 font-medium mb-3">
                        Billed annually — 2 months free
                      </p>
                    )}
                    {plan.plan === "trial" && (
                      <p className="text-[11px] text-muted-foreground mb-3">14-day free trial, no credit card required</p>
                    )}

                    {/* Service badges */}
                    <div className="flex flex-wrap gap-1.5">
                      {SERVICE_BADGES.map(({ key, label, icon: Icon }) => (
                        <span
                          key={key}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${
                            services[key]
                              ? "bg-primary/10 text-primary border-primary/30"
                              : "text-muted-foreground/40 border-border/50 line-through"
                          }`}
                        >
                          <Icon className="w-2.5 h-2.5" /> {label}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Features */}
                  <ul className="space-y-2 mb-7 flex-grow text-sm">
                    {(plan.features ?? []).map((f: string) => (
                      <PricingFeature key={f}>{f}</PricingFeature>
                    ))}

                    {/* Capacity lines */}
                    {plan.max_team > 0 && (
                      <PricingFeature>
                        {plan.max_team === -1 ? "Unlimited" : plan.max_team} team member{plan.max_team !== 1 ? "s" : ""}
                      </PricingFeature>
                    )}
                    {plan.max_journalists > 0 && (
                      <PricingFeature>
                        {plan.max_journalists === -1 ? "Unlimited" : plan.max_journalists} journalists
                      </PricingFeature>
                    )}
                    {plan.max_events > 0 && (
                      <PricingFeature>
                        {plan.max_events === -1 ? "Unlimited" : plan.max_events} events/year
                      </PricingFeature>
                    )}
                    {plan.max_press_releases > 0 && (
                      <PricingFeature>
                        {plan.max_press_releases === -1 ? "Unlimited" : plan.max_press_releases} press releases/mo
                      </PricingFeature>
                    )}
                  </ul>

                  <Link to={display.ctaHref} className="w-full">
                    <Button
                      variant={display.highlight ? "default" : "outline"}
                      size="lg"
                      className="w-full"
                    >
                      {display.cta}
                    </Button>
                  </Link>
                </Card>
              );
            })}
          </div>
        )}

        {/* Feature comparison note */}
        <p className="text-center text-xs text-muted-foreground mt-8">
          All plans include a 14-day free trial period. No credit card required to start.
          Annual billing is charged as a single payment.
        </p>

        {/* Plan comparison table */}
        <div className="mt-20 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">What's included</h2>
          <div className="overflow-x-auto rounded-xl border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-4 py-3 font-semibold w-1/3">Feature</th>
                  <th className="text-center px-3 py-3 font-semibold">Individual</th>
                  <th className="text-center px-3 py-3 font-semibold">Starter</th>
                  <th className="text-center px-3 py-3 font-semibold">Professional</th>
                  <th className="text-center px-3 py-3 font-semibold">Enterprise</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {COMPARISON_ROWS.map(row => (
                  <tr key={row.label} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 text-muted-foreground">{row.label}</td>
                    {(["individual","starter","professional","enterprise"] as const).map(p => (
                      <td key={p} className="text-center px-3 py-3">
                        {row[p] === true  ? <Check className="w-4 h-4 text-primary mx-auto" /> :
                         row[p] === false ? <X    className="w-4 h-4 text-muted-foreground/40 mx-auto" /> :
                         <span className="text-xs font-medium">{row[p]}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Payment methods */}
        <div className="mt-20 text-center">
          <h3 className="text-xl font-bold mb-2">Accepted payment methods</h3>
          <p className="text-muted-foreground mb-6 text-sm">
            Pay with card or mobile money — whichever works best for you
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {PAYMENT_METHODS.map(m => (
              <div key={m} className="px-3 py-1.5 rounded-lg border border-border text-sm font-medium text-muted-foreground">
                {m}
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-20 max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Frequently asked questions</h2>
          <div className="space-y-6">
            {FAQ.map(({ q, a }) => (
              <div key={q}>
                <h4 className="font-semibold mb-1">{q}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

const PricingFeature = ({ children }: { children: React.ReactNode }) => (
  <li className="flex items-start gap-2 text-sm">
    <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
    <span>{children}</span>
  </li>
);

const COMPARISON_ROWS: Array<{
  label: string;
  individual: boolean | string;
  starter: boolean | string;
  professional: boolean | string;
  enterprise: boolean | string;
}> = [
  { label: "Price / month",         individual: "$19",     starter: "$99",      professional: "$249",    enterprise: "Custom" },
  { label: "Team members",           individual: "1",       starter: "10",       professional: "25",      enterprise: "Unlimited" },
  { label: "Journalists",            individual: "10",      starter: "100",      professional: "300",     enterprise: "Unlimited" },
  { label: "Events / year",          individual: "4",       starter: "24",       professional: "60",      enterprise: "Unlimited" },
  { label: "Press releases / mo",    individual: "10",      starter: "50",       professional: "150",     enterprise: "Unlimited" },
  { label: "Social Hub",             individual: true,      starter: true,       professional: true,      enterprise: true },
  { label: "Media Registry",         individual: true,      starter: true,       professional: true,      enterprise: true },
  { label: "Accreditation",          individual: true,      starter: true,       professional: true,      enterprise: true },
  { label: "Events management",      individual: true,      starter: true,       professional: true,      enterprise: true },
  { label: "Press Releases",         individual: true,      starter: true,       professional: true,      enterprise: true },
  { label: "Media Kits",             individual: false,     starter: true,       professional: true,      enterprise: true },
  { label: "Campaigns & Scheduling", individual: false,     starter: true,       professional: true,      enterprise: true },
  { label: "Approval Workflows",     individual: false,     starter: true,       professional: true,      enterprise: true },
  { label: "Awards Programme",       individual: false,     starter: false,      professional: true,      enterprise: true },
  { label: "Coverage Analytics",     individual: false,     starter: false,      professional: true,      enterprise: true },
  { label: "AI Content Generation",  individual: false,     starter: false,      professional: true,      enterprise: true },
  { label: "Intelligence & Listening",individual: false,    starter: false,      professional: true,      enterprise: true },
  { label: "API Access",             individual: false,     starter: false,      professional: false,     enterprise: true },
  { label: "White-label / SSO",      individual: false,     starter: false,      professional: false,     enterprise: true },
  { label: "Custom SLA",             individual: false,     starter: false,      professional: false,     enterprise: true },
];

const FAQ = [
  {
    q: "Can I switch plans later?",
    a: "Yes. You can upgrade or downgrade at any time. When you upgrade, you're charged the prorated difference immediately. Downgrades take effect at the next billing cycle.",
  },
  {
    q: "What happens when I exceed a plan limit?",
    a: "You'll receive a warning before hitting the limit. Once reached, you can either upgrade your plan or wait until the next billing period resets your quota.",
  },
  {
    q: "Do you offer discounts for associations or NGOs?",
    a: "Yes. Contact our sales team at sales@conorbit.com for non-profit, association, and volume pricing. We also offer custom packages for large media groups.",
  },
  {
    q: "Is there a free trial?",
    a: "Every plan starts with a 14-day free trial, no credit card required. You get access to all features included in your chosen plan during the trial period.",
  },
  {
    q: "Which payment methods are accepted?",
    a: "We accept M-Pesa, Airtel Money, MTN Mobile Money, Visa/Mastercard, PayPal, and Flutterwave — covering most of Africa and the rest of the world.",
  },
];

export default Pricing;
